import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const IS_IPHONE = /iPhone/i.test(navigator.userAgent);

// Swipe threshold in pixels — touch deltas smaller than this are treated as
// taps or scroll-init, not page flips.
const SWIPE_THRESHOLD_PX = 50;

// Left this fraction of the width goes back a page; the rest goes forward.
// Deliberately lopsided - forward is the common case and wants the big target.
const TAP_BACK_ZONE = 0.3;

interface PdfViewerProps {
  src: string;
  title: string;
  style?: React.CSSProperties;
  // 'continuous' (default): all pages stacked vertically in a scrollable column.
  // 'paged': one page at a time, swipe / arrow keys flip pages — for band stand use.
  mode?: 'continuous' | 'paged';
}

const PdfViewer: React.FC<PdfViewerProps> = ({ src, title, style, mode = 'continuous' }) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  // Natural height/width of page 1, used to fit a page to the screen. Null until
  // the PDF loads, in which case we fall back to the old width-only fit.
  const [pageAspect, setPageAspect] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  // Reset to page 1 whenever the source changes
  useEffect(() => { setCurrentPage(1); setPageAspect(null); }, [src]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
        setContainerHeight(entry.contentRect.height);
      }
    });
    observer.observe(el);
    setContainerWidth(el.clientWidth);
    setContainerHeight(el.clientHeight);
    return () => observer.disconnect();
  }, []);

  const goNext = useCallback(() => {
    setCurrentPage(p => Math.min(p + 1, numPages || p));
  }, [numPages]);
  const goPrev = useCallback(() => {
    setCurrentPage(p => Math.max(p - 1, 1));
  }, []);

  // Arrow-key navigation in paged mode (desktop fallback for the iPad swipe)
  useEffect(() => {
    if (mode !== 'paged') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'ArrowRight' || e.key === 'PageDown' || e.key === ' ') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        goPrev();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mode, goNext, goPrev]);

  const handleTouchStart: React.TouchEventHandler<HTMLDivElement> = (e) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  };
  const handleTouchEnd: React.TouchEventHandler<HTMLDivElement> = (e) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    // Only treat as a swipe if it's predominantly horizontal and clears the
    // threshold — vertical scrolls within tall pages get through untouched.
    if (Math.abs(dx) > SWIPE_THRESHOLD_PX && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) goNext();
      else goPrev();
    }
  };

  // Tap to turn the page - less movement than a swipe, which matters when you
  // are holding an instrument. Matches how forScore/OnSong and friends behave:
  // a wide "next" zone on the right, a narrower "back" zone on the left.
  const handleTapTurn: React.MouseEventHandler<HTMLDivElement> = (e) => {
    const el = e.currentTarget;
    const x = e.clientX - el.getBoundingClientRect().left;
    if (x < el.clientWidth * TAP_BACK_ZONE) goPrev();
    else goNext();
  };

  if (IS_IPHONE) {
    return (
      <a
        href={src}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textDecoration: 'none',
          color: '#ffd700',
          fontSize: '16px',
          fontWeight: 'bold',
          backgroundColor: '#28190f',
          border: '1px solid #5a3a1a',
          borderRadius: '4px',
        }}
      >
        Tap to open {title}
      </a>
    );
  }

  const documentLoading = (
    <div style={{ padding: 20, textAlign: 'center', color: '#a08060' }}>Loading chart...</div>
  );
  const documentError = (
    <div style={{ padding: 20, textAlign: 'center', color: '#e74c3c' }}>Failed to load PDF</div>
  );

  if (mode === 'paged') {
    // Fit the WHOLE page on screen, not just its width. Charts are portrait
    // (aspect 1.41-1.60), so scaling to width alone made every page taller than
    // the screen and forced a scroll mid-song. Until the PDF reports its size we
    // fall back to the width fit.
    const fittedWidth = pageAspect && containerHeight > 0
      ? Math.min(containerWidth, containerHeight / pageAspect)
      : containerWidth;

    return (
      <div
        ref={containerRef}
        style={{
          ...style,
          // Once the page is fitted nothing overflows, so scrolling is off. If the
          // fit is unknown we are still width-scaled and MUST stay scrollable,
          // otherwise the bottom of the chart would be unreachable.
          overflow: pageAspect ? 'hidden' : 'auto',
          WebkitOverflowScrolling: 'touch',
          touchAction: pageAspect ? 'manipulation' : 'pan-y',
          position: 'relative',
          display: 'flex',
          alignItems: pageAspect ? 'center' : 'flex-start',
          justifyContent: 'center',
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onClick={handleTapTurn}
      >
        <Document
          file={src}
          onLoadSuccess={(pdf: any) => {
            setNumPages(pdf.numPages);
            // Page 1's dimensions stand in for the document - chart PDFs are uniform.
            pdf.getPage(1)
              .then((page: any) => {
                const vp = page.getViewport({ scale: 1 });
                if (vp.width > 0) setPageAspect(vp.height / vp.width);
              })
              .catch(() => { /* keep the width fit */ });
          }}
          loading={documentLoading}
          error={documentError}
        >
          {fittedWidth > 0 && numPages > 0 && (
            <Page
              key={`${currentPage}-${Math.round(fittedWidth)}`}
              pageNumber={currentPage}
              width={fittedWidth}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          )}
        </Document>
        {numPages > 1 && (
          <>
            <div aria-hidden style={{
              position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
              color: 'rgba(0,0,0,0.22)', fontSize: 30, lineHeight: 1, pointerEvents: 'none',
            }}>‹</div>
            <div aria-hidden style={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              color: 'rgba(0,0,0,0.22)', fontSize: 30, lineHeight: 1, pointerEvents: 'none',
            }}>›</div>
          </>
        )}
        {numPages > 1 && (
          <div
            style={{
              position: 'absolute',
              bottom: 10,
              right: 14,
              background: 'rgba(0,0,0,0.65)',
              color: '#f5deb3',
              padding: '4px 10px',
              borderRadius: 12,
              fontSize: 12,
              letterSpacing: 0.5,
              pointerEvents: 'none',
              fontFamily: 'monospace',
            }}
          >
            {currentPage} / {numPages}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        ...style,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <Document
        file={src}
        onLoadSuccess={({ numPages: n }) => setNumPages(n)}
        loading={documentLoading}
        error={documentError}
      >
        {containerWidth > 0 && Array.from({ length: numPages }, (_, i) => (
          <Page
            key={i + 1}
            pageNumber={i + 1}
            width={containerWidth}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        ))}
      </Document>
    </div>
  );
};

export default PdfViewer;
